<template>
  <div class="system-mapping settings-container">
    <HKLLeftRight v-if="canEdit">
      <div />
      <HKLButton
        v-if="!isEditing"
        color="orange"
        action="edit"
        @click="() => isEditing = true"
      />
      <div v-else>
        <HKLButton
          color="darkblue"
          action="cancel"
          @click="cancel"
        />
        <HKLButton
          color="orange"
          action="save"
          @click="save"
        />
      </div>
    </HKLLeftRight>
    <hr>
    <div class="clients-box">
      <a-row
        type="flex"
        :gutter="[16,16]"
      >
        <a-col :span="colClientSpan">
          <label :class="requireFlagClass">
            {{ $t('table.header.clientId') }}
          </label>
        </a-col>
        <a-col :span="colClientSpan">
          <label>
            {{ $t('table.header.description') | capitalize }}
          </label>
        </a-col>
        <a-col :span="colTpSpan">
          <label :class="requireFlagClass">
            {{ $t('table.header.tenantProject') }}
          </label>
        </a-col>
        <a-col
          v-if="isEditing"
          :span="2"
        />
      </a-row>
      <template
        v-if="isEditing"
      >
        <a-row
          v-for="(item, index) in pendingClientData"
          :key="index"
          type="flex"
          :gutter="[16,16]"
        >
          <a-col :span="colClientSpan">
            <HKLInput
              v-model="item.clientId"
              v-fusioninputcheck="{
                required: true,
                showTip: false,
                patternOnly: validateClientId(item)
              }"
              class="h42"
            />
          </a-col>
          <a-col :span="colClientSpan">
            <HKLTextarea
              v-model="item.description"
              class="h42"
            />
          </a-col>
          <a-col
            :span="colTpSpan"
          >
            <HKLMultiSelect
              v-model="item.tenantProjects"
              :required="true"
              :showTip="false"
              :fusionInputCheckPattern="multiSelectNotEmptyPattern"
              :options="getOptions(item)"
              @focus="setFocusItem(item)"
            />
          </a-col>
          <a-col
            v-if="isEditing"
            :span="2"
          >
            <HKLDeleteIcon @click="removeClient(index)" />
          </a-col>
        </a-row>
      </template>
      <template v-else>
        <a-row
          v-for="(item, index) in parsedClientData"
          :key="index"
          type="flex"
          :gutter="[16,16]"
        >
          <a-col :span="colClientSpan">
            <div>
              {{ item.clientId }}
            </div>
          </a-col>
          <a-col :span="colClientSpan">
            <div>
              {{ item.description | isEmptyValue }}
            </div>
          </a-col>
          <a-col
            :span="colTpSpan"
          >
            <ul>
              <li
                v-for="(name, j) in item.displayTps"
                :key="`${index}_${j}`"
              >
                {{ name }}
              </li>
            </ul>
          </a-col>
        </a-row>
      </template>
      <div
        v-if="isEditing"
        class="underline pointer add-client-btn"
        @click="addNewClient"
      >
        {{ $t('settings.systemMapping.clientId') }} +
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, unref } from 'vue-demi'
import lodash from 'lodash'
import { getExtSysTenantProjects, getTenantProjects, upsertExtSysTenantProjects } from '@/api/settings/integration.request'
import HKLButton from '@/controls/Button/HKLButton.vue'
import HKLDeleteIcon from '@/controls/Icon/HKLDeleteIcon.vue'
import HKLInput from '@/controls/Input/HKLInput.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { multiSelectNotEmptyPattern } from '@/plugin/FusionInputCheck.pattern.utils'
import { tenantProjectStatus } from '@/utils/locale'
import { getProjectName } from '@/modules/tenantProjects/utils'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import HKLTextarea from '@/controls/Input/HKLTextarea.vue'
export default {
  components: {
    HKLLeftRight,
    HKLButton,
    HKLInput,
    HKLTextarea,
    HKLMultiSelect,
    HKLDeleteIcon,
  },
  setup () {
    const isEditing = ref(false)
    const allTps = ref([])
    const clientsData = ref([])
    const pendingClientData = ref([])
    const removingClientData = ref([])
    const focusItem = ref({})
    const refreshPendingData = async () => {
      clientsData.value = await getExtSysTenantProjects()
      pendingClientData.value = unref(clientsData).map(item => {
        const editableKeys = ['id', 'clientId', 'description']
        const pickedItem = lodash.pick(item, editableKeys)
        return {
          ...pickedItem,
          tenantProjects: item.tenantProjects.map(tp => tp.id),
        }
      })
    }
    onMounted(async () => {
      const allowedStatus = [
        tenantProjectStatus.SUBMITTED,
        tenantProjectStatus.IN_PROGRESS,
        tenantProjectStatus.COMPLETED,
        tenantProjectStatus.CANCELLED,
        tenantProjectStatus.WITHDRAWN,
      ]
      allTps.value = await getTenantProjects({ status: allowedStatus })
      await refreshPendingData()
    })
    const parsedClientData = computed(() => {
      return clientsData.value.map(item => {
        const displayTps = item.tenantProjects.map(
          tenantProject => getProjectName(tenantProject, tenantProject.tenancy),
        )
        return {
          displayTps,
          ...item,
        }
      })
    })
    const validTpOptions = computed(() => {
      return allTps.value.filter(item => {
        const invalidStatus = [tenantProjectStatus.COMPLETED, tenantProjectStatus.WITHDRAWN, tenantProjectStatus.CANCELLED]
        return !invalidStatus.includes(item.status)
      }).map((item) => ({
        name: getProjectName(item, item.tenancy),
        value: item.id,
      }))
    })
    const allTpsById = computed(() => {
      return lodash.keyBy(allTps.value, 'id')
    })
    const validTpOptsById = computed(() => {
      return lodash.keyBy(validTpOptions.value, 'value')
    })
    const setFocusItem = (item) => {
      focusItem.value = item
    }
    const getOptions = (item) => {
      const selectedOpts = item.tenantProjects.map(id => {
        const selectedItem = allTpsById.value[id]
        return {
          name: getProjectName(selectedItem, selectedItem.tenancy),
          value: id,
        }
      })
      let result = selectedOpts
      if (focusItem.value.id === item.id) {
        const filteredOpts = selectedOpts.filter(item => !validTpOptsById.value[item.value])
        result = filteredOpts.concat(validTpOptions.value)
      }
      return lodash.orderBy(result, ['name'], ['asc'])
    }
    const addNewClient = () => {
      pendingClientData.value.push({
        clientId: '',
        description: '',
        tenantProjects: [],
      })
    }
    const removeClient = (index) => {
      const removingItem = pendingClientData.value[index]
      removingItem.tenantProjects = []
      removingClientData.value.push(removingItem)
      pendingClientData.value.splice(index, 1)
    }
    const validateClientId = (curItem) => {
      return () => {
        if (!curItem.clientId) return false
        const seenIds = new Set()
        return pendingClientData.value.every(item => {
          const isSelf = curItem.clientId === item.clientId
          if (seenIds.has(item.clientId) && isSelf) {
            console.warn(`duplicated id is ${item.clientId}`)
            notification.error(i18n.t('hint.systemMapping.duplicated'))
            return false
          }
          seenIds.add(item.clientId)
          return true
        })
      }
    }
    const save = async () => {
      fusionInputCheckUtil.validateAllFields()
      const isValid = fusionInputCheckUtil.isAllFieldsValid()
      if (!isValid) {
        return
      }
      isEditing.value = false
      await upsertExtSysTenantProjects([
        ...pendingClientData.value,
        ...removingClientData.value,
      ])
      await refreshPendingData()
    }
    const cancel = async () => {
      isEditing.value = false
      fusionInputCheckUtil.resetAllFields()
      await refreshPendingData()
    }
    const canEdit = computed(() => {
      return aclHelper.getAccess(aclPermsType.settings.update)
    })
    return {
      requireFlagClass: computed(() => {
        return { 'required-flag': unref(isEditing) }
      }),
      colClientSpan: 4,
      colTpSpan: 14,
      canEdit,
      isEditing,
      clientsData,
      pendingClientData,
      parsedClientData,
      getOptions,
      validTpOptions,
      setFocusItem,
      addNewClient,
      removeClient,
      save,
      cancel,
      validateClientId,
      multiSelectNotEmptyPattern,
    }
  },
}
</script>

<style lang="scss" scoped>
@import './settings.scss';
.clients-box {
  padding: 0 24px;
  .add-client-btn {
    margin-top: 5px;
  }
}
</style>
